import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";

/* ===================== CONFIG ===================== */

const VIEWBOX_SIZE = 900;

const FIXED_RADIUS = 160;
const FINAL_SIDE_LENGTH = 250;

const HOVER_SCALE = 1.1;
const TSPAN_LINE_HEIGHT = 30;

const INTERACTIVE_EXTRA_ROTATION_DEGREES = 0;
const INTERACTIVE_SPIN_DURATION_MS = 2000;

const ROUTE_READY_DELAY_MS = 14000;

const POSITION_TO_TOP_LEFT_OFFSET: Record<string, number> = {
  "top-left": 0,
  "top-right": -120,
  bottom: 120,
};

const ROUTE_TO_CIRCLE_ID: Record<string, string> = {
  "/bpo": "c1-svg",
  "/address-finder": "c2-svg",
  "/web-development": "c3-svg",
};

/* ===================== DATA ===================== */

const DYNAMIC_CIRCLE_DATA = [
  {
    id: "c1-svg",
    fill: "#b7b7b7",
    targetPosition: "top-left",
    label: ["BPO"],
    textFill: "#fff",
  },
  {
    id: "c3-svg",
    fill: "#0b2a59",
    targetPosition: "top-right",
    label: ["Web", "Development"],
    textFill: "#fff",
  },
  {
    id: "c2-svg",
    fill: "#5a7fae",
    targetPosition: "bottom",
    label: ["Address", "Finder"],
    textFill: "#fff",
  },
];

/* ===================== GEOMETRY ===================== */

const calculatePositionsFromSideLength = (
  size: number,
  sideLength: number
) => {
  const center = size / 2;
  const half = sideLength / 2;
  const height = (sideLength * Math.sqrt(3)) / 2;

  const base = height / 3;
  const apex = (height * 2) / 3;

  return {
    "top-left": { cx: center - base, cy: center - half },
    "top-right": { cx: center + apex, cy: center },
    bottom: { cx: center - base, cy: center + half },
  };
};

/* ===================== COMPONENT ===================== */

interface CloudNavigationFinishedProps {
  onCircleSelect?: (id: string) => void;
  activeRoute?: string;
}

const CloudNavigationFinished: React.FC<
  CloudNavigationFinishedProps
> = ({ onCircleSelect, activeRoute }) => {
  /* ---------- STATE ---------- */

  const [hoveredCircle, setHoveredCircle] = useState<string | null>(null);
  const [focusedCircleId, setFocusedCircleId] = useState<string | null>(null);

  const [currentRotationAngle, setCurrentRotationAngle] = useState(0);
  const rotationRef = useRef(0);
  const animationRef = useRef<number | null>(null);

  // ← 14s delay gate
  const [isReadyForRoute, setIsReadyForRoute] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsReadyForRoute(true), ROUTE_READY_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  const ROTATION_PIVOT = `${VIEWBOX_SIZE / 2} ${VIEWBOX_SIZE / 2}`;

  /* ---------- POSITIONS ---------- */

  const finalPositions = useMemo(
    () =>
      calculatePositionsFromSideLength(
        VIEWBOX_SIZE,
        FINAL_SIDE_LENGTH
      ),
    []
  );

  const fullData = useMemo(() => {
    return DYNAMIC_CIRCLE_DATA.map((data) => {
      const pos =
        finalPositions[
          data.targetPosition as keyof typeof finalPositions
        ];

      const totalTextHeight =
        (data.label.length - 1) * TSPAN_LINE_HEIGHT;

      return {
        ...data,
        cx: pos.cx,
        cy: pos.cy,
        textY: pos.cy - totalTextHeight / 2,
        tspanDy: data.label.map((_, i) =>
          i === 0 ? 0 : TSPAN_LINE_HEIGHT
        ),
      };
    });
  }, [finalPositions]);

  /* ---------- ROTATION ---------- */

  const easeOutQuad = (t: number) => t * (2 - t);

  const animateInteractiveSpin = useCallback(
    (start: number, target: number) => {
      let startTime: number;

      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }

      const spin = (ts: number) => {
        if (!startTime) startTime = ts;

        const progress = Math.min(
          1,
          (ts - startTime) / INTERACTIVE_SPIN_DURATION_MS
        );

        const eased = easeOutQuad(progress);
        const angle = start + (target - start) * eased;

        setCurrentRotationAngle(angle);
        rotationRef.current = angle;

        if (progress < 1) {
          animationRef.current = requestAnimationFrame(spin);
        } else {
          animationRef.current = null;
        }
      };

      animationRef.current = requestAnimationFrame(spin);
    },
    []
  );

  /* ---------- CLICK ---------- */

  const handleCircleClick = useCallback(
    (id: string) => {
      const data = fullData.find((c) => c.id === id)!;
      const offset =
        POSITION_TO_TOP_LEFT_OFFSET[data.targetPosition];

      const startAngle = rotationRef.current;

      if (focusedCircleId === id) {
        setFocusedCircleId(null);
        return;
      }

      let delta = offset - (startAngle % 360);
      if (delta > 180) delta -= 360;
      if (delta < -180) delta += 360;

      const target =
        startAngle +
        delta +
        (delta >= 0
          ? INTERACTIVE_EXTRA_ROTATION_DEGREES
          : -INTERACTIVE_EXTRA_ROTATION_DEGREES);

      setFocusedCircleId(id);
      onCircleSelect?.(id);
      animateInteractiveSpin(startAngle, target);
    },
    [focusedCircleId, animateInteractiveSpin, fullData, onCircleSelect]
  );

  /* ---------- ROUTE EFFECT (14s gated) ---------- */

  useEffect(() => {
    if (!isReadyForRoute) return;

    if (!activeRoute) {
      if (focusedCircleId) {
        setFocusedCircleId(null);
      }
      return;
    }

    const circleId = ROUTE_TO_CIRCLE_ID[activeRoute];
    if (!circleId || focusedCircleId === circleId) return;

    const data = fullData.find((c) => c.id === circleId)!;
    const offset = POSITION_TO_TOP_LEFT_OFFSET[data.targetPosition];
    const startAngle = rotationRef.current;

    let delta = offset - (startAngle % 360);
    if (delta > 180) delta -= 360;
    if (delta < -180) delta += 360;

    const target =
      startAngle +
      delta +
      (delta >= 0
        ? INTERACTIVE_EXTRA_ROTATION_DEGREES
        : -INTERACTIVE_EXTRA_ROTATION_DEGREES);

    setFocusedCircleId(circleId);
    animateInteractiveSpin(startAngle, target);
  }, [activeRoute, isReadyForRoute, focusedCircleId, fullData, animateInteractiveSpin]);

  /* ---------- RENDER ---------- */

  return (
    <svg
     viewBox={`0 0 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`}
     id="finish-svg"
            className={`
            w-[440px] h-[440px]        /* mobile (default) */
            sm:w-[400px] sm:h-[400px] /* ≥640px */
            md:w-[500px] md:h-[500px] /* ≥768px */
            lg:w-[650px] lg:h-[650px] /* ≥1024px */
            xl:w-[800px] xl:h-[800px] /* ≥1280px */
            `}

            >


      <g transform={`rotate(${currentRotationAngle}, ${ROTATION_PIVOT})`}>
        {fullData.map((data) => (
          <circle
            key={data.id}
            cx={data.cx}
            cy={data.cy}
            r={FIXED_RADIUS}
            fill={data.fill}
            className="cursor-pointer"
            onMouseEnter={() => setHoveredCircle(data.id)}
            onMouseLeave={() => setHoveredCircle(null)}
            onClick={() => handleCircleClick(data.id)}
            transform={
              hoveredCircle === data.id
                ? `translate(${data.cx},${data.cy}) scale(${HOVER_SCALE}) translate(-${data.cx},-${data.cy})`
                : undefined
            }
          />
        ))}

        {fullData.map((data) => {
          const visible =
            hoveredCircle === data.id ||
            focusedCircleId === data.id;

          return (
            <text
              key={data.id}
              x={data.cx}
              y={data.textY}
              textAnchor="middle"
              fill={data.textFill}
              fontSize={visible ? 26 : 16}
              fontWeight="bold"
              opacity={visible ? 1 : 0}
              style={{ transition: "opacity 0.3s, font-size 0.25s" }}
              transform={`rotate(${-currentRotationAngle}, ${data.cx}, ${data.cy})`}
              pointerEvents="none"
            >
              {data.label.map((line, i) => (
                <tspan key={i} x={data.cx} dy={data.tspanDy[i]}>
                  {line}
                </tspan>
              ))}
            </text>
          );
        })}
      </g>
    </svg>
  );
};

export default CloudNavigationFinished;